import React from 'react'
import {toast} from 'react-toastify'
import {Tooltip} from '../core/shared/Tooltip'
import {
  CardNumberElement,
  CardMonthElement,
  CardYearElement,
  CardCvvElement,
  Elements,
} from '@recurly/react-recurly'
import {
  Button,
  Icon,
  ParagraphText,
  Checkbox,
  Select,
} from '@thryvlabs/maverick'
// import rawCountryStates from 'iso3166-2-db/i18n/en'
import {
  FormFieldContainer,
  TaxesPromptContainer,
} from '../ui-view/payment-information'
// import {useFlags} from 'launchdarkly-react-client-sdk'
import {AutopayTerms} from './autopay-terms'
import {useBillingInfo} from './keap-partner/payment-information-components/contexts/billing-info-context'
import {usePaymentAddress} from './keap-partner/payment-information-components/contexts/payment-address-context'
import {usePaymentCheckboxes} from './keap-partner/payment-information-components/contexts/payment-checkboxes-context'
import {useRecurlyPayment} from './keap-partner/payment-information-components/contexts/recurly-payment-context'
import {usePaymentCard} from './keap-partner/payment-information-components/contexts/payment-card-context'
import {COUNTRIES} from './keap-partner/payment-information-components/constants'
import {
  CardOnFileForm,
  ChangeToCardOnFileButton,
} from './keap-partner/payment-information-components/card-on-file-form'
import {useDispatch, useSelector} from 'react-redux'
import {nextPaymentStep, setPaymentStep} from '../redux/paymentScreenViewSlice'
import {fetchSelectedCountryRegionList} from '../utils/address-validation'
// import {usePaymentOrder} from './keap-partner/payment-information-components/hooks/use-payment-order'
import {VisaLogo} from '../assets/VisaLogo'
import mastercardLogo from '../assets/mastercard_logo.png'
import amexLogo from '../assets/amex_logo.png'
import discoverLogo from '../assets/discover_logo.png'
import {GenericCardIcon} from '../assets/GenericCardIcon'
import {CardValidIcon} from '../assets/CardValidIcon'

const RecurlyPaymentForm = ({
  calculatingTaxes,
  setCalculatingTaxes,
  showFirstSection,
  showSecondSection,
  nextStep,
  setName,
  businessAddress,
  country,
  setIsAddressUpdated,
  bizphone,
  isAddressUpdated,
  setPaymentRequestState,
  setCurrentStep,
  disclaimerText,
  allowCountryChange = false,
  skipOldMobile = false,
}) => {
  const is5Order = true
  const {isMobileViewEnable} = useSelector(state => state.screenView)
  const {billingInfo, tryCardOnFile, isCVCVerified} = useBillingInfo()
  const [selectedCountry, setSelectedCountry] = React.useState(
    COUNTRIES.find(({value}) => value === country),
  )
  const {
    addressInformation,
    isValidAddress,
    updateAddressField,
    resetAddress,
    setFromBusinessAddress,
  } = usePaymentAddress()
  const {
    clearRecurlyToken,
    fetchToken,
    recurlyToken,
    recurlyFetchError,
    setRecurlyFetchError,
  } = useRecurlyPayment()
  const {isValidCard, updateValidation, cardValidation} = usePaymentCard()
  const [cardBrand, setCardBrand] = React.useState(null)
  const {checkBoxesValues, setCheckBoxesValues} = usePaymentCheckboxes()
  const [listOfStates, setListOfStates] = React.useState([])
  const formRef = React.useRef()

  const {useBusinessAddressInfo} = checkBoxesValues
  const dispatch = useDispatch()

  // const {frontendUpdatedTerms} = useFlags()
  const frontendUpdatedTerms = true

  const isAusNZ = ['AU', 'NZ'].includes(country)

  const handleOnchange = ({target: {value, name}}) => {
    setCheckBoxesValues({
      ...checkBoxesValues,
      agreedTerms: false,
    })
    clearRecurlyToken()
    if (recurlyFetchError) {
      setRecurlyFetchError(undefined)
    }
    updateAddressField(name, value)
    if (!isMobileViewEnable) setIsAddressUpdated(false)
  }

  React.useEffect(() => {
    if (!isCVCVerified) return undefined
    dispatch(setPaymentStep(4))
  }, [dispatch, isCVCVerified])

  React.useEffect(() => {
    setSelectedCountry(COUNTRIES.find(({value}) => country === value))
  }, [country])

  React.useEffect(() => {
    const getStates = () => {
      let stateLabel = ''
      if (['US', 'AU', 'NZ'].includes(selectedCountry.value)) {
        stateLabel = 'State'
      } else if (selectedCountry.value === 'KY') {
        stateLabel = 'City'
      } else if (selectedCountry.value === 'CA') {
        stateLabel = 'Province'
      } else if (selectedCountry.value === 'BB') {
        stateLabel = 'Parish'
      } else {
        stateLabel = 'State'
      }
      let blankOption = [{name: 'Select ' + stateLabel, value: ''}]
      // const states = rawCountryStates[selectedCountry.value].regions
      //   .map(({iso, name}) => ({
      //     name,
      //     value: iso,
      //   }))
      //   .sort((a, b) => (a.name > b.name ? 1 : -1))
      let data = fetchSelectedCountryRegionList(selectedCountry)
      // console.log(data)
      setListOfStates([...blankOption, ...data])
      // setListOfStates([...blankOption, ...states])
    }
    if (selectedCountry.value) {
      getStates(selectedCountry.value)
    }
  }, [selectedCountry])

  React.useEffect(() => {
    if (useBusinessAddressInfo) {
      if (checkBoxesValues.updateBillingAddress) {
        setCheckBoxesValues({...checkBoxesValues, updateBillingAddress: false})
      }
      setFromBusinessAddress(businessAddress)
    } else {
      resetAddress()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBusinessAddressInfo, businessAddress, listOfStates])

  React.useEffect(() => {
    if (!checkBoxesValues.agreedTerms) return undefined
    if (recurlyToken) return undefined
    if (billingInfo && tryCardOnFile) return undefined
    fetchToken(formRef.current).catch(error => {
      if (isMobileViewEnable && error?.fields?.includes('postal_code')) {
        dispatch(setPaymentStep(2))
      }
      setCheckBoxesValues({
        ...checkBoxesValues,
        agreedTerms: false,
      })
    })
  }, [recurlyToken, checkBoxesValues.agreedTerms, billingInfo, tryCardOnFile])

  if (billingInfo && tryCardOnFile) {
    return (
      <CardOnFileForm
        setCalculatingTaxes={setCalculatingTaxes}
        setName={setName}
        setIsAddressUpdated={setIsAddressUpdated}
        calculatingTaxes={calculatingTaxes}
        setPaymentRequestState={setPaymentRequestState}
        disclaimerText={disclaimerText}
      />
    )
  }

  if (isMobileViewEnable && !skipOldMobile) {
    return (
      <form
        onSubmit={e => {
          e.preventDefault()
          // nextStep()
          // dispatch(nextPaymentStep())
        }}
        ref={formRef}
      >
        <ChangeToCardOnFileButton />

        <div style={{display: showFirstSection ? 'block' : 'none'}}>
          <CardNumberElement
            className="recurly-element-inline"
            style={{
              fontSize: '18px',
              width: '100%',
              placeholder: {content: 'Card number'},
            }}
            onChange={e => {
              updateValidation('cardNumber', e.valid)
              clearRecurlyToken()
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            displayIcon={true}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          <div className="d-flex justify-content-between">
            <CardMonthElement
              className="recurly-element-month"
              inputType="mobileSelect"
              style={{fontSize: '18', placeholder: {content: 'MM'}}}
              onChange={e => {
                updateValidation('cardMonth', e.valid)
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
            <CardYearElement
              className="recurly-element-inline ml-2"
              inputType="mobileSelect"
              style={{fontSize: '18', placeholder: {content: 'YY'}}}
              onChange={e => {
                updateValidation('cardYear', e.valid)
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
          </div>
          <div className="d-flex justify-content-between">
            <CardCvvElement
              className="recurly-element-inline mr-2"
              style={{
                fontSize: '18',
                placeholder: {content: 'CVV'},
              }}
              onChange={e => {
                updateValidation('cardCVV', e.valid)
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
            <div>
              <input
                type="text"
                className="recurly-element w-full"
                data-recurly="postal_code"
                placeholder="Postal Code"
                style={{
                  fontSize: '16px',
                }}
                name="postalCode"
                value={addressInformation.postalCode}
                onChange={handleOnchange}
                disabled={checkBoxesValues.agreedTerms}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                    : undefined
                }
              />
              {recurlyFetchError?.fields?.includes('postal_code') && (
                <span className="error-message">Invalid Postal Code</span>
              )}
            </div>
          </div>
          <Select
            options={COUNTRIES}
            selectLabel="Country"
            width="full"
            className="recurly-element recurly-address mt-3"
            data-recurly="country"
            onChange={() => {
              clearRecurlyToken()
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            selectedOption={selectedCountry}
            setSelectedOption={setSelectedCountry}
            disabled={!allowCountryChange || checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          <input
            hidden
            readOnly
            type="text"
            placeholder="Country"
            value={selectedCountry.value}
            style={{
              fontSize: '18px',
            }}
            className="recurly-element recurly-address ml-2"
            data-recurly="country"
          />
          <input
            hidden
            readOnly
            type="text"
            placeholder="Phone"
            value={bizphone}
            style={{
              fontSize: '18px',
            }}
            className="recurly-element recurly-address ml-2"
            data-recurly="phone"
          />
          <div className="d-flex justify-content-center mb-4 mt-4">
            <Button
              variant="primary"
              onClick={() => {
                dispatch(nextPaymentStep())
              }}
              className="step__container__button"
              disabled={!isValidCard}
            >
              Continue
            </Button>
          </div>
        </div>

        <div style={{display: showSecondSection ? 'block' : 'none'}}>
          {!isAusNZ && (
            <div
              className="d-flex info__alert gap-2 px-2"
              style={{
                background: 'rgb(230, 242, 255)',
                border: '1px solid rgb(193, 222, 255)',
                borderRadius: '5px',
              }}
            >
              <Icon type="solid" variant="infoCircle" color="#057aff" />
              <ParagraphText
                variant="reg"
                style={{marginTop: '10px', fontSize: '12px'}}
              >
                The current estimated taxes are based on your business address.
              </ParagraphText>
            </div>
          )}
          <div className="mt-4 mb-2">
            <Checkbox
              hasLabel
              name="useBusinessAddressInfo"
              disabled={checkBoxesValues.agreedTerms}
              checked={checkBoxesValues.useBusinessAddressInfo}
              onChange={e => {
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  useBusinessAddressInfo: e.target.checked,
                  agreedTerms: false,
                })
              }}
              label="Same as my business address"
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
          </div>
          <div className="d-flex justify-content-between">
            <input
              style={{width: ' 49%'}}
              onChange={handleOnchange}
              className="recurly-element recurly-input"
              data-recurly="first_name"
              placeholder="First Name"
              name="firstName"
              value={addressInformation.firstName}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
            <input
              style={{width: '49%'}}
              className="recurly-element recurly-input"
              data-recurly="last_name"
              placeholder="Last Name"
              name="lastName"
              value={addressInformation.lastName}
              onChange={handleOnchange}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
          </div>
          <input
            type="text"
            className="recurly-element recurly-address"
            data-recurly="address1"
            placeholder="Address"
            style={{
              fontSize: '18px',
            }}
            name="address"
            value={addressInformation.address}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          <input
            type="text"
            className="recurly-element recurly-input mt-2.8"
            data-recurly="city"
            placeholder="City"
            style={{
              fontSize: '18px',
            }}
            name="city"
            value={addressInformation.city}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          <select
            className="recurly-element recurly-address mt-2.8"
            value={addressInformation?.state}
            onChange={e => {
              handleOnchange({
                target: {
                  name: 'state',
                  value: e.target.selectedOptions[0].value,
                },
              })
              clearRecurlyToken()
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          >
            {listOfStates.map(({name, value}) => {
              return (
                <option key={value} value={value}>
                  {name}
                </option>
              )
            })}
          </select>
          <input
            hidden
            readOnly
            value={addressInformation?.state}
            type="text"
            className="recurly-element"
            data-recurly="state"
            placeholder="State"
            style={{
              fontSize: '18px',
            }}
          />

          <div className="mt-4 mb-2">
            <Checkbox
              hasLabel
              name="updateBillingAddress"
              disabled={checkBoxesValues.agreedTerms || useBusinessAddressInfo}
              checked={checkBoxesValues.updateBillingAddress}
              onChange={e => {
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  updateBillingAddress: e.target.checked,
                  agreedTerms: false,
                })
                clearRecurlyToken()
              }}
              label="Update my billing address"
            />
          </div>

          {disclaimerText && (
            <p className="card-number-helper-text" style={{margin: '16px 0 0'}}>
              {disclaimerText}
            </p>
          )}

          <div className="d-flex justify-content-center mb-4 mt-4">
            <Button
              variant="primary"
              type="submit"
              className="step__container__button"
              onClick={() => {
                dispatch(nextPaymentStep())
              }}
              disabled={!isValidAddress}
            >
              Continue
            </Button>
          </div>
        </div>
      </form>
    )
  }

  return (
    <form
      ref={formRef}
      style={{display: 'flex', flexDirection: 'column', gap: '10px'}}
    >
      {/* ENTER CARD DETAILS */}
      <div style={{position: 'relative'}}>
        {checkBoxesValues.agreedTerms && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              cursor: 'not-allowed',
              borderRadius: '10px',
            }}
            onClick={() =>
              toast.warn(
                'Please uncheck the Terms and Conditions to change your payment information.',
              )
            }
          />
        )}
        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '16px',
            opacity: checkBoxesValues.agreedTerms ? 0.6 : 1,
            pointerEvents: checkBoxesValues.agreedTerms ? 'none' : 'auto',
          }}
        >
          <div className="section-heading section-heading--no-divider">
            <div className="section-heading-title-container">
              <Tooltip
                variant="bottom"
                description="Provide card details to proceed with payment"
                width="220px"
              >
                <div
                  className="d-flex align-items-center"
                  style={{columnGap: '4px'}}
                >
                  <p className="section-heading-title">Enter Card Details</p>
                  <Icon
                    type="regular"
                    variant="infoCircle"
                    color="#808080"
                    width="11"
                    height="11"
                  />
                </div>
              </Tooltip>
            </div>
            <ChangeToCardOnFileButton />
          </div>
          <div
            className="card-name-row"
            style={{
              marginTop: is5Order && !isAusNZ && !billingInfo ? '10px' : '0',
            }}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <label className="payment-variant-label">First Name</label>
              <input
                className="recurly-element recurly-input"
                style={{
                  height: '32px',
                  border: '1px solid #bfbfbf',
                  borderRadius: '5px',
                  background: '#ffffff',
                  padding: '8px',
                  fontFamily: 'Open Sans',
                  fontSize: '12px',
                  fontWeight: '400',
                  marginTop: 0,
                }}
                data-recurly="first_name"
                placeholder="Cardholder First Name"
                name="firstName"
                onChange={handleOnchange}
                value={addressInformation.firstName}
                disabled={checkBoxesValues.agreedTerms}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                    : undefined
                }
              />
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <label className="payment-variant-label">Last Name</label>
              <input
                className="recurly-element recurly-input"
                style={{
                  height: '32px',
                  border: '1px solid #bfbfbf',
                  borderRadius: '5px',
                  background: '#ffffff',
                  padding: '8px',
                  fontFamily: 'Open Sans',
                  fontSize: '12px',
                  fontWeight: '400',
                  marginTop: 0,
                }}
                data-recurly="last_name"
                placeholder="Cardholder Last Name"
                name="lastName"
                onChange={handleOnchange}
                value={addressInformation.lastName}
                disabled={checkBoxesValues.agreedTerms}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                    : undefined
                }
              />
            </div>
          </div>
          <FormFieldContainer>
            <div className="card-details-grid" style={{gridColumn: 'span 5'}}>
              <div
                style={{display: 'flex', flexDirection: 'column', gap: '4px'}}
              >
                <Tooltip
                  variant="top"
                  description="Enter the 16-digit card number on the card"
                  width="220px"
                  className="align-self-start"
                >
                  <label className="payment-variant-label">
                    Enter Valid Card Number
                  </label>
                </Tooltip>
                <Tooltip
                  variant="top"
                  description={
                    checkBoxesValues.agreedTerms
                      ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                      : 'Enter the 16-digit card number on the card'
                  }
                  width="220px"
                  block
                  suppressNativeTitle
                >
                  <div className="card-number-wrapper">
                    <div className="card-brand-slot">
                      {cardBrand === 'visa' && <VisaLogo width={30} />}
                      {cardBrand === 'master' && (
                        <img
                          src={mastercardLogo}
                          alt="Mastercard logo"
                          style={{width: '30px', objectFit: 'contain'}}
                        />
                      )}
                      {cardBrand === 'american_express' && (
                        <img
                          src={amexLogo}
                          alt="American Express logo"
                          style={{width: '30px', objectFit: 'contain'}}
                        />
                      )}
                      {cardBrand === 'discover' && (
                        <img
                          src={discoverLogo}
                          alt="Discover logo"
                          style={{width: '30px', objectFit: 'contain'}}
                        />
                      )}
                      {(!cardBrand || cardBrand === 'unknown') && (
                        <GenericCardIcon />
                      )}
                    </div>
                    <CardNumberElement
                      onChange={e => {
                        setCardBrand(e.brand)
                        updateValidation('cardNumber', e.valid)
                        clearRecurlyToken()
                      }}
                      className="card-number-input"
                      style={{
                        fontFamily: 'Open Sans',
                        fontSize: '12px',
                        fontWeight: '400',
                        color: '#000000',
                        placeholder: {color: '#9ca3af', content: 'Card number'},
                        marginTop: 0,
                      }}
                      disabled={checkBoxesValues.agreedTerms}
                    />
                    {cardValidation.cardNumber && (
                      <div className="card-valid-slot">
                        <CardValidIcon />
                      </div>
                    )}
                  </div>
                </Tooltip>
              </div>
              <div className="card-details-grid">
                <div
                  style={{display: 'flex', flexDirection: 'column', gap: '4px'}}
                >
                  <Tooltip
                    variant="top"
                    description="Enter the card's expiration date"
                    width="220px"
                    className="align-self-start"
                  >
                    <label className="payment-variant-label">Expiry Date</label>
                  </Tooltip>
                  <Tooltip
                    variant="top"
                    description={
                      checkBoxesValues.agreedTerms
                        ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                        : "Enter the card's expiration date"
                    }
                    width="220px"
                    block
                    suppressNativeTitle
                  >
                    <div className="card-expiry-group">
                      <CardMonthElement
                        onChange={e => {
                          updateValidation('cardMonth', e.valid)
                          clearRecurlyToken()
                        }}
                        className="card-info-input w-full"
                        style={{
                          fontFamily: 'Open Sans',
                          fontSize: '12px',
                          fontWeight: '400',
                          color: '#000000',
                          placeholder: {color: '#9ca3af', content: 'MM'},
                          marginTop: 0,
                        }}
                        disabled={checkBoxesValues.agreedTerms}
                      />
                      <span className="card-expiry-separator">/</span>
                      <CardYearElement
                        onChange={e => {
                          updateValidation('cardYear', e.valid)
                          clearRecurlyToken()
                        }}
                        className="card-info-input w-full"
                        style={{
                          fontFamily: 'Open Sans',
                          fontSize: '12px',
                          fontWeight: '400',
                          color: '#000000',
                          placeholder: {color: '#9ca3af', content: 'YY'},
                          marginTop: 0,
                        }}
                        disabled={checkBoxesValues.agreedTerms}
                      />
                    </div>
                  </Tooltip>
                </div>
                <div
                  style={{display: 'flex', flexDirection: 'column', gap: '4px'}}
                >
                  <Tooltip
                    variant="top"
                    description="Enter the 3–4 digit code"
                    width="220px"
                    className="align-self-start"
                  >
                    <label className="payment-variant-label">CVV / CVC</label>
                  </Tooltip>
                  <Tooltip
                    variant="top"
                    description={
                      checkBoxesValues.agreedTerms
                        ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                        : 'Enter the 3–4 digit code'
                    }
                    width="220px"
                    block
                    suppressNativeTitle
                  >
                    <CardCvvElement
                      onChange={e => {
                        updateValidation('cardCVV', e.valid)
                        clearRecurlyToken()
                      }}
                      className="card-info-input w-full"
                      style={{
                        fontFamily: 'Open Sans',
                        fontSize: '12px',
                        fontWeight: '400',
                        color: '#000000',
                        placeholder: {color: '#9ca3af', content: 'CVV'},
                        marginTop: 0,
                      }}
                      disabled={checkBoxesValues.agreedTerms}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          </FormFieldContainer>
          {is5Order && frontendUpdatedTerms && <AutopayTerms />}
        </div>
      </div>

      {/* BILLING ADDRESS */}
      <div style={{position: 'relative'}}>
        {checkBoxesValues.agreedTerms && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              cursor: 'not-allowed',
              borderRadius: '10px',
            }}
            onClick={() =>
              toast.warn(
                'Please uncheck the Terms and Conditions to change your payment information.',
              )
            }
          />
        )}
        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: '16px',
            opacity: checkBoxesValues.agreedTerms ? 0.6 : 1,
            pointerEvents: checkBoxesValues.agreedTerms ? 'none' : 'auto',
          }}
        >
          {is5Order && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div className="section-heading section-heading--no-divider">
                <div className="section-heading-title-container">
                  <div
                    className="d-flex align-items-center"
                    style={{columnGap: '16px'}}
                  >
                    <Tooltip
                      variant="top"
                      description="Provide the billing address for this payment"
                      width="220px"
                    >
                      <div
                        className="d-flex align-items-center"
                        style={{columnGap: '4px'}}
                      >
                        <p className="section-heading-title">Billing Address</p>
                        <Icon
                          type="regular"
                          variant="infoCircle"
                          color="#808080"
                          width="11"
                          height="11"
                        />
                      </div>
                    </Tooltip>
                    <div
                      className="d-flex align-items-center"
                      style={{columnGap: '6px'}}
                    >
                      <input
                        type="checkbox"
                        id="useBusinessAddressInfoDesktop"
                        name="useBusinessAddressInfo"
                        disabled={checkBoxesValues.agreedTerms}
                        checked={checkBoxesValues.useBusinessAddressInfo}
                        onChange={e => {
                          setCheckBoxesValues({
                            ...checkBoxesValues,
                            useBusinessAddressInfo: e.target.checked,
                            agreedTerms: false,
                          })
                          clearRecurlyToken()
                        }}
                        style={{
                          cursor: 'pointer',
                          width: '16px',
                          height: '16px',
                          accentColor: '#5378FC',
                        }}
                      />
                      <label
                        htmlFor="useBusinessAddressInfoDesktop"
                        className="business-address-label"
                        style={{cursor: 'pointer', marginBottom: '0px'}}
                      >
                        Same as my business address
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {is5Order && isAddressUpdated && (
            <TaxesPromptContainer>
              <Icon type="regular" variant="infoCircle" color="#057AFF" />
              <ParagraphText variant="reg" className="m-0">
                The current estimated taxes are based on your business address.
              </ParagraphText>
            </TaxesPromptContainer>
          )}
          {!is5Order && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <label className="payment-variant-label">Country</label>
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <select
                  className="recurly-element recurly-address"
                  data-recurly="country"
                  value={selectedCountry?.value}
                  onChange={e => {
                    setSelectedCountry(
                      COUNTRIES.find(c => c.value === e.target.value),
                    )
                    clearRecurlyToken()
                    setCheckBoxesValues({
                      ...checkBoxesValues,
                      agreedTerms: false,
                    })
                  }}
                  disabled={!allowCountryChange || checkBoxesValues.agreedTerms}
                  style={{
                    width: '100%',
                    height: '32px',
                    border: '1px solid #bfbfbf',
                    borderRadius: '5px',
                    fontFamily: 'Open Sans',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '100%',
                    paddingLeft: '8px',
                    paddingRight: '28px',
                    marginTop: 0,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                  }}
                >
                  {COUNTRIES.map(({name, value}) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{
                    position: 'absolute',
                    right: '8px',
                    pointerEvents: 'none',
                  }}
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          )}
          <input
            hidden
            readOnly
            type="text"
            placeholder="Country"
            value={selectedCountry.value}
            style={{
              fontSize: '18px',
            }}
            className="recurly-element recurly-address ml-2"
            data-recurly="country"
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginBottom: '20px',
            }}
          >
            <label className="payment-variant-label">Street Address</label>
            <input
              type="text"
              className="recurly-element"
              data-recurly="address1"
              placeholder="Street Address"
              style={{
                height: '32px',
                border: '1px solid #bfbfbf',
                borderRadius: '5px',
                background: '#ffffff',
                padding: '8px',
                fontFamily: 'Open Sans',
                fontSize: '12px',
                fontWeight: '400',
                width: '100%',
                marginTop: 0,
              }}
              name="address"
              value={addressInformation.address}
              onChange={handleOnchange}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
          </div>
          <div
            className="grid grid-cols-3 gap-2"
            style={{marginBottom: '20px'}}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <label className="payment-variant-label">City</label>
              <input
                type="text"
                className="recurly-element"
                data-recurly="city"
                placeholder="City"
                style={{
                  height: '32px',
                  border: '1px solid #bfbfbf',
                  borderRadius: '5px',
                  background: '#ffffff',
                  padding: '8px',
                  fontFamily: 'Open Sans',
                  fontSize: '12px',
                  fontWeight: '400',
                  marginTop: 0,
                }}
                name="city"
                value={addressInformation.city}
                onChange={handleOnchange}
                disabled={checkBoxesValues.agreedTerms}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                    : undefined
                }
              />
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <label className="payment-variant-label">State</label>
              <select
                className="recurly-element"
                style={{
                  height: '32px',
                  border: '1px solid #bfbfbf',
                  borderRadius: '5px',
                  fontFamily: 'Open Sans',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '100%',
                  paddingLeft: '8px',
                  marginTop: 0,
                }}
                value={addressInformation?.state}
                onChange={e => {
                  handleOnchange({
                    target: {
                      name: 'state',
                      value: e.target.selectedOptions[0].value,
                    },
                  })
                  clearRecurlyToken()
                  setCheckBoxesValues({
                    ...checkBoxesValues,
                    agreedTerms: false,
                  })
                }}
                disabled={checkBoxesValues.agreedTerms}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                    : undefined
                }
              >
                {listOfStates.map(({name, value}) => {
                  return (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  )
                })}
              </select>
              <input
                hidden
                readOnly
                value={addressInformation?.state}
                type="text"
                className="recurly-element"
                data-recurly="state"
                placeholder="State"
                style={{
                  fontSize: '16px',
                }}
              />
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <label className="payment-variant-label">Postal Code</label>
              <input
                type="text"
                className="recurly-element w-full"
                data-recurly="postal_code"
                placeholder="Postal Code"
                style={{
                  height: '32px',
                  border: '1px solid #bfbfbf',
                  borderRadius: '5px',
                  background: '#ffffff',
                  padding: '8px',
                  fontFamily: 'Open Sans',
                  fontSize: '12px',
                  fontWeight: '400',
                  marginTop: 0,
                }}
                name="postalCode"
                value={addressInformation.postalCode}
                onChange={handleOnchange}
                disabled={checkBoxesValues.agreedTerms}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                    : undefined
                }
              />
              {recurlyFetchError?.fields?.includes('postal_code') && (
                <span className="error-message">Invalid Postal Code</span>
              )}
            </div>
          </div>
          {is5Order && (
            <div
              style={{
                display: 'flex',
                flexDirection: isMobileViewEnable ? 'column' : undefined,
                gap: '16px',
                alignItems: isMobileViewEnable ? 'stretch' : 'flex-start',
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  opacity: checkBoxesValues.agreedTerms ? 0.6 : 1,
                  pointerEvents: checkBoxesValues.agreedTerms ? 'none' : 'auto',
                }}
              >
                <label className="payment-variant-label">Country</label>
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <select
                    className="payment-country-select"
                    data-recurly="country"
                    value={selectedCountry?.value}
                    onChange={e => {
                      setSelectedCountry(
                        COUNTRIES.find(c => c.value === e.target.value),
                      )
                      clearRecurlyToken()
                      setCheckBoxesValues({
                        ...checkBoxesValues,
                        agreedTerms: false,
                      })
                    }}
                    disabled={
                      !allowCountryChange || checkBoxesValues.agreedTerms
                    }
                    style={{
                      width: '100%',
                      height: '32px',
                      border: '1px solid #bfbfbf',
                      borderRadius: '5px',
                      fontFamily: 'Open Sans',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '100%',
                      paddingLeft: '8px',
                      paddingRight: '28px',
                      marginTop: 0,
                      appearance: 'none',
                      WebkitAppearance: 'none',
                    }}
                  >
                    {COUNTRIES.map(({name, value}) => (
                      <option key={value} value={value}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    style={{
                      position: 'absolute',
                      right: '8px',
                      pointerEvents: 'none',
                    }}
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <div
                style={{
                  flex: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  paddingTop: '16px',
                }}
              >
                {disclaimerText && (
                  <p className="card-number-helper-text" style={{margin: 0}}>
                    {disclaimerText}
                  </p>
                )}
                <p className="card-number-helper-text" style={{margin: 0}}>
                  {
                    "By processing your payment, you agree that your personal information will be shared with Thryv's payment processor—Adyen, N.V. and will be subject to Adyen's "
                  }
                  <a
                    target="_blank"
                    href="https://www.adyen.com/policies-and-disclaimer/privacy-policy"
                    rel="noreferrer"
                    style={{fontWeight: 700, color: '#5378FC'}}
                  >
                    privacy statement
                  </a>
                  {'.'}
                </p>
              </div>
            </div>
          )}
          {!is5Order && (
            <div className="mt-4 mb-2">
              <Checkbox
                hasLabel
                name="useBusinessAddressInfo"
                disabled={checkBoxesValues.agreedTerms}
                checked={checkBoxesValues.useBusinessAddressInfo}
                onChange={e => {
                  setCheckBoxesValues({
                    ...checkBoxesValues,
                    useBusinessAddressInfo: e.target.checked,
                    agreedTerms: false,
                  })
                  clearRecurlyToken()
                }}
                label="Same as my business address"
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                    : undefined
                }
              />
              {frontendUpdatedTerms && <AutopayTerms styles={{marginTop: 0}} />}
            </div>
          )}
        </div>
      </div>
    </form>
  )
}

const PaymentForm = ({
  nextStep,
  calculatingTaxes,
  setCalculatingTaxes,
  showFirstSection = false,
  showSecondSection = false,
  checkBoxesValues,
  setCheckBoxesValues,
  setName,
  businessAddress,
  country,
  handleCompleteOrder,
  setIsAddressUpdated,
  bizphone,
  is5Order,
  isAddressUpdated,
  paymentPreview,
  paymentRequestState,
  setPaymentRequestState,
  setCurrentStep,
  disclaimerText,
  skipOldMobile = false,
}) => {
  return (
    <Elements>
      <RecurlyPaymentForm
        calculatingTaxes={calculatingTaxes}
        setCalculatingTaxes={setCalculatingTaxes}
        showFirstSection={showFirstSection}
        showSecondSection={showSecondSection}
        nextStep={nextStep}
        checkBoxesValues={checkBoxesValues}
        setCheckBoxesValues={setCheckBoxesValues}
        setName={setName}
        businessAddress={businessAddress}
        country={country}
        handleCompleteOrder={handleCompleteOrder}
        setIsAddressUpdated={setIsAddressUpdated}
        bizphone={bizphone}
        is5Order={is5Order}
        isAddressUpdated={isAddressUpdated}
        paymentPreview={paymentPreview}
        paymentRequestState={paymentRequestState}
        setPaymentRequestState={setPaymentRequestState}
        setCurrentStep={setCurrentStep}
        disclaimerText={disclaimerText}
        skipOldMobile={skipOldMobile}
      />
    </Elements>
  )
}

export {PaymentForm}
